// Import model
import { FpRoom } from '../models/FpRoom';

// Import base class serializer

// Import dependency types (models and enums used in properties)
import { Door } from '../models/Door';
import { Surface } from '../models/Surface';

// Import dependency serializers (for complex types)
import { DoorSerializer } from './DoorSerializer';
import { SurfaceSerializer } from './SurfaceSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for FpRoom - handles JSON serialization and deserialization with type validation
 */
export class FpRoomSerializer {
  /**
   * Deserializes a JSON string into a FpRoom instance
   */
  static deserialize(json: string): FpRoom {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a FpRoom instance
   */
  static fromObject(obj: any): FpRoom {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to FpRoom');
    }


    const instance: FpRoom = {
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      lastModifiedOn: obj['lastModifiedOn'] as string | undefined,
      usdzRemoteFileId: obj['usdzRemoteFileId'] as string | undefined,
      usdzRemoteFileIdName: obj['usdzRemoteFileId|Name'] as string | undefined,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      parentId: obj['parentId'] as string | undefined,
      parentIdName: obj['parentId|Name'] as string | undefined,
      parentObjectType: obj['parentObjectType'] as string | undefined,
      parentObjectTypeName: obj['parentObjectType|Name'] as string | undefined,
      relatedObjects: obj['relatedObjects'] as string | undefined,
      modelRemoteFileId: obj['modelRemoteFileId'] as string | undefined,
      modelRemoteFileIdName: obj['modelRemoteFileId|Name'] as string | undefined,
      thumbnailRemoteFileId: obj['thumbnailRemoteFileId'] as string | undefined,
      thumbnailRemoteFileIdName: obj['thumbnailRemoteFileId|Name'] as string | undefined,
      existingSubfloorId: obj['existingSubfloorId'] as string | undefined,
      existingSubfloorIdName: obj['existingSubfloorId|Name'] as string | undefined,
      removeExistingFloorId: obj['removeExistingFloorId'] as string | undefined,
      removeExistingFloorIdName: obj['removeExistingFloorId|Name'] as string | undefined,
      furnitureToMoveId: obj['furnitureToMoveId'] as string | undefined,
      furnitureToMoveIdName: obj['furnitureToMoveId|Name'] as string | undefined,
      appliances: obj['appliances'] as number | undefined,
      bathroomFixtures: obj['bathroomFixtures'] as number | undefined,
      heavyItemsToMove: obj['heavyItemsToMove'] as number | undefined,
      tags: obj['tags'] ? (obj['tags'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      perimeterUom: obj['perimeterUOM'] as string | undefined,
      perimeter: obj['perimeter'] as number | undefined,
      totalAreaUom: obj['totalAreaUOM'] as string | undefined,
      totalArea: obj['totalArea'] as number | undefined,
      doors: obj['doors'] ? (obj['doors'] as any[]).map((item: any) =>
        DoorSerializer.fromObject(item)
      ) : [] as any,
      surfaces: obj['surfaces'] ? (obj['surfaces'] as any[]).map((item: any) =>
        SurfaceSerializer.fromObject(item)
      ) : [] as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of FpRoom instances
   */
  static deserializeArray(json: string): FpRoom[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a FpRoom instance to a JSON string
   */
  static serialize(instance: FpRoom): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a FpRoom instance to a plain object
   */
  static toObject(instance: FpRoom): any {
    return {
      'name': instance.name,
      'description': instance.description,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'lastModifiedOn': instance.lastModifiedOn,
      'usdzRemoteFileId': instance.usdzRemoteFileId,
      'usdzRemoteFileId|Name': instance.usdzRemoteFileIdName,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      'parentId': instance.parentId,
      'parentId|Name': instance.parentIdName,
      'parentObjectType': instance.parentObjectType,
      'parentObjectType|Name': instance.parentObjectTypeName,
      'relatedObjects': instance.relatedObjects,
      'modelRemoteFileId': instance.modelRemoteFileId,
      'modelRemoteFileId|Name': instance.modelRemoteFileIdName,
      'thumbnailRemoteFileId': instance.thumbnailRemoteFileId,
      'thumbnailRemoteFileId|Name': instance.thumbnailRemoteFileIdName,
      'existingSubfloorId': instance.existingSubfloorId,
      'existingSubfloorId|Name': instance.existingSubfloorIdName,
      'removeExistingFloorId': instance.removeExistingFloorId,
      'removeExistingFloorId|Name': instance.removeExistingFloorIdName,
      'furnitureToMoveId': instance.furnitureToMoveId,
      'furnitureToMoveId|Name': instance.furnitureToMoveIdName,
      'appliances': instance.appliances,
      'bathroomFixtures': instance.bathroomFixtures,
      'heavyItemsToMove': instance.heavyItemsToMove,
      'tags': instance.tags?.map((item: any) =>
        item
      ),
      'perimeterUOM': instance.perimeterUom,
      'perimeter': instance.perimeter,
      'totalAreaUOM': instance.totalAreaUom,
      'totalArea': instance.totalArea,
      'doors': instance.doors?.map((item: any) =>
        DoorSerializer.toObject(item)
      ),
      'surfaces': instance.surfaces?.map((item: any) =>
        SurfaceSerializer.toObject(item)
      ),
    };
  }

  /**
   * Serializes an array of FpRoom instances to a JSON string
   */
  static serializeArray(instances: FpRoom[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
